import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';
import type { WithAsChild } from '@/ui-kit/types/utils';
import { cn } from '@/ui-kit/utils';

const listVariants = cva('m-0 list-none p-0', {
  variants: {
    variant: {
      default: null,
      // Set the last item's border to transparent to remove it without affecting the item's height
      card: 'bg-surface-default overflow-hidden rounded-[14px] px-4 *:last:data-[slot=list-item]:border-b-transparent',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

type ListProps = ComponentProps<'ul'> & VariantProps<typeof listVariants>;

export const List = ({ className, variant, ...props }: ListProps) => (
  <ul className={cn(listVariants({ variant, className }))} {...props} />
);

export const listItemVariants = cva(
  'group/list-item border-b-border-default flex w-full border-b text-left',
  {
    variants: {
      size: {
        /**
         * Reduce padding bottom by 1px to compensate for the border-bottom on the list item.
         * When using a ListItemTrigger, remove the padding from the ListItem and add it to the ListItemTrigger instead.
         */
        sm: 'pt-3 pb-[calc(--spacing(3)-1px)] has-data-[slot=list-item-trigger]:py-0 *:data-[slot=list-item-trigger]:pt-3 *:data-[slot=list-item-trigger]:pb-[calc(--spacing(3)-1px)]',
        lg: 'pt-6 pb-[calc(--spacing(6)-1px)] has-data-[slot=list-item-trigger]:py-0 *:data-[slot=list-item-trigger]:pt-6 *:data-[slot=list-item-trigger]:pb-[calc(--spacing(6)-1px)]',
      },
    },
    defaultVariants: {
      size: 'sm',
    },
  },
);

type ListItemProps = WithAsChild<ComponentProps<'li'> & VariantProps<typeof listItemVariants>>;

export const ListItem = ({ className, size, asChild = false, ...props }: ListItemProps) => {
  const Component = asChild ? Slot : 'li';
  return <Component data-slot="list-item" className={cn(listItemVariants({ size, className }))} {...props} />;
};

export const ListItemStart = ({ className, ...props }: ComponentProps<'div'>) => (
  <div
    className={cn(
      'flex flex-1 items-center gap-2',
      'group-has-data-[slot=list-item-subtitle]/list-item:items-start',
      className,
    )}
    {...props}
  />
);

export const ListItemTextGroup = ({ className, ...props }: ComponentProps<'div'>) => (
  <div className={cn('flex flex-col gap-0.5', className)} {...props} />
);

export const ListItemEnd = ({ className, ...props }: ComponentProps<'div'>) => (
  <div className={cn('flex items-center gap-2 text-end', className)} {...props} />
);

export const ListItemTitle = ({ className, ...props }: ComponentProps<'span'>) => (
  <span
    className={cn('group-has-data-[slot=list-item-subtitle]/list-item:text-body-small-semibold', className)}
    {...props}
  />
);

export const ListItemSubtitle = ({ className, ...props }: ComponentProps<'span'>) => (
  <span
    data-slot="list-item-subtitle"
    className={cn('text-caption-regular text-text-subtle', className)}
    {...props}
  />
);

const ListItemTrigger = ({ className, asChild = false, ...props }: WithAsChild<ComponentProps<'button'>>) => {
  const Component = asChild ? Slot : 'button';

  return (
    <Component
      data-slot="list-item-trigger"
      type={asChild ? undefined : 'button'}
      className={cn('flex w-full text-left', className)}
      {...props}
    />
  );
};

type InteractiveListItemProps = ComponentProps<typeof ListItemTrigger> &
  VariantProps<typeof listItemVariants>;

export const InteractiveListItem = ({ size, ...props }: InteractiveListItemProps) => (
  <ListItem size={size}>
    <ListItemTrigger {...props} />
  </ListItem>
);
